import React from "react";
import { Sparkles, X, Users, ListPlus, Trash2, Search } from "lucide-react";

const AddObjectiveForm = ({
  pendingNewObjective,
  newObjectiveAnimating,
  handleNewObjectiveChange,
  handleAiPredictions,
  predictedTokens,
  predictedTitle,
  titleSuggestionDismissed,
  setTitleSuggestionDismissed,
  projects,
  activeEmployees = [],
  projectParticipants = [],
  iterationsById = {},
  sprintOptions,
  handleCancelNewObjective,
  handleSaveNewObjective,
  handleAddSubTask,
  handleSubTaskChange,
  handleRemoveSubTask,
}) => {
  const [projectSearch, setProjectSearch] = React.useState("");
  const [projectDropdownOpen, setProjectDropdownOpen] = React.useState(false);

  React.useEffect(() => {
    if (!pendingNewObjective) {
      setProjectSearch("");
      setProjectDropdownOpen(false);
    }
  }, [pendingNewObjective]);

  React.useEffect(() => {
    if (!projects || projects.length === 0) {
      setProjectSearch("");
      return;
    }
    if (!pendingNewObjective?.project_id) {
      setProjectSearch("");
      return;
    }
    const currentProject = projects.find(
      project => Number(project.id) === Number(pendingNewObjective.project_id)
    );
    if (currentProject) {
      setProjectSearch(currentProject.name || "");
    }
  }, [projects, pendingNewObjective?.project_id]);

  const normalizedProjectSearch = projectSearch.trim().toLowerCase();

  const projectGroups = React.useMemo(() => {
    const buckets = {
      in_progress: [],
      proposition: [],
      completed: [],
      other: [],
    };

    projects.forEach(project => {
      const key = buckets[project.status] ? project.status : "other";
      buckets[key].push(project);
    });

    const sortProjects = (list) =>
      [...list].sort((a, b) => (a.name || "").localeCompare(b.name || ""));

    const matchesSearch = (project) => {
      if (!normalizedProjectSearch) return true;
      const haystack = [
        project.name,
        project.project_number,
        project.client_name,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(normalizedProjectSearch);
    };

    const labelMap = {
      in_progress: "Projets en cours",
      proposition: "Projets proposés",
      completed: "Projets terminés",
      other: "Autres projets",
    };

    const order = ["in_progress", "proposition", "other", "completed"];

    return order
      .map(key => ({
        key,
        label: labelMap[key],
        items: sortProjects(buckets[key]).filter(matchesSearch),
      }))
      .filter(group => group.items.length > 0);
  }, [projects, normalizedProjectSearch]);

  const closeProjectDropdown = React.useCallback(() => {
    setProjectDropdownOpen(false);
  }, []);

  const openProjectDropdown = () => {
    setProjectDropdownOpen(true);
  };

  const toggleProjectDropdown = () => {
    setProjectDropdownOpen(prev => !prev);
  };

  const handleProjectInputChange = (event) => {
    setProjectSearch(event.target.value);
    setProjectDropdownOpen(true);
  };

  const handleProjectSelect = (project) => {
    handleNewObjectiveChange("project_id", String(project.id));
    setProjectSearch(project.name || "");
    closeProjectDropdown();
  };

  const projectList = React.useMemo(() => {
    const groups =
      projectGroups.length === 0
        ? [
            {
              key: "all",
              label: "Projets",
              items: projects.filter(project => {
                if (!normalizedProjectSearch) return true;
                const haystack = [project.name, project.project_number, project.client_name]
                  .filter(Boolean)
                  .join(" ")
                  .toLowerCase();
                return haystack.includes(normalizedProjectSearch);
              }),
            },
          ]
        : projectGroups;

    const flat = groups.flatMap(group =>
      group.items.map(project => ({
        ...project,
        groupKey: group.key,
        groupLabel: group.label,
      }))
    );

    return { groups, flat };
  }, [projectGroups, projects, normalizedProjectSearch]);

  const noProjectMatch =
    normalizedProjectSearch.length > 0 && projectList.flat.length === 0;

  if (!pendingNewObjective) return null;

  const hasProjectSelected = Boolean(pendingNewObjective.project_id);
  const participantIds = new Set(
    (projectParticipants || []).map((participant) => Number(participant.id))
  );

  const sortedActiveEmployees = [...(activeEmployees || [])].sort((a, b) => {
    const aParticipant = participantIds.has(Number(a.id));
    const bParticipant = participantIds.has(Number(b.id));
    if (aParticipant === bParticipant) {
      return a.name.localeCompare(b.name);
    }
    return aParticipant ? -1 : 1;
  });

  const sprintId = pendingNewObjective.sprint_id;
  const selectedSprint = sprintId
    ? sprintOptions.find((sp) => Number(sp.id) === Number(sprintId))
    : null;
  const subTasks = Array.isArray(pendingNewObjective.subTasks)
    ? pendingNewObjective.subTasks
    : [];

  const renderEmployeeOptionLabel = (employee) => {
    const isParticipant = participantIds.has(Number(employee.id));
    const displayName = employee.name || "Employé";
    return `${isParticipant ? "⭐ " : ""}${displayName}`;
  };

  const formatDate = (value) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    return date.toLocaleDateString();
  };

  const sprintLabel = (sprint) => {
    if (!sprint) return "";
    const baseLabel = sprint.description ? sprint.description : `Sprint ${sprint.id}`;
    const iterationName =
      sprint.iterationName ||
      (sprint.iteration_id &&
        (iterationsById[sprint.iteration_id]?.name || `Itération ${sprint.iteration_id}`));

    const start = sprint.start_date ? formatDate(sprint.start_date) : null;
    const end = sprint.end_date ? formatDate(sprint.end_date) : null;
    const range = start && end ? `${start} → ${end}` : null;

    return [baseLabel, iterationName, range].filter(Boolean).join(" · ");
  };

  const projectPrefix = (statusKey) => {
    switch (statusKey) {
      case "in_progress":
        return "⚡ ";
      case "proposition":
        return "🚀 ";
      case "completed":
        return "✅ ";
      default:
        return "";
    }
  };

  return (
    <div
      id="new-objective-card"
      className={`relative w-full max-w-4xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl transition-all duration-500 ${
        newObjectiveAnimating ? "translate-y-6 opacity-0" : "opacity-100"
      }`}
    >
      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-blue-500 via-indigo-500 to-sky-400" />
      <div className="space-y-6 p-6 md:p-8">
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-slate-800">Nouvel objectif</h3>
            <p className="text-sm text-slate-500">
              Renseignez les détails clés, ajoutez des sous-tâches et mettez votre équipe sur les rails.
            </p>
          </div>
          {predictedTokens != null && (
            <div className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-sm text-blue-700">
              <Sparkles className="h-4 w-4 text-blue-500" />
              <span>{predictedTokens} tokens suggérés</span>
            </div>
          )}
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-600">Nom de l'objectif</label>
              <div className="relative">
                <input
                  type="text"
                  value={pendingNewObjective.name}
                  onChange={(e) => handleNewObjectiveChange("name", e.target.value)}
                  placeholder="Ex. Clarifier le périmètre MVP"
                  className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-800 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-blue-400 focus:ring-2 focus:ring-blue-200"
                  autoFocus
                  onBlur={() => handleAiPredictions(pendingNewObjective.name)}
                />
                {predictedTitle && !titleSuggestionDismissed && (
                  <div className="absolute left-0 right-0 top-full z-10 mt-2">
                    <div className="flex items-center justify-between rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700 shadow-md">
                      <button
                        type="button"
                        onClick={() => handleNewObjectiveChange("name", predictedTitle)}
                        className="flex flex-1 items-center gap-2 text-left font-medium hover:text-amber-900"
                        title="Accepter la suggestion"
                      >
                        <Sparkles className="h-4 w-4 flex-shrink-0 text-amber-500" />
                        <span className="truncate">{predictedTitle}</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setTitleSuggestionDismissed(true)}
                        className="ml-3 rounded-full p-1 hover:bg-amber-100"
                        title="Masquer"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-600">Description (optionnel)</label>
              <textarea
                value={pendingNewObjective.description || ""}
                onChange={(e) => handleNewObjectiveChange("description", e.target.value)}
                placeholder="Partagez le contexte, les résultats attendus ou les liens utiles…"
                className="min-h-[72px] w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-blue-400 focus:ring-2 focus:ring-blue-200"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSaveNewObjective();
                  }
                }}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-600">Effort (tokens)</label>
              <div className="flex flex-wrap items-center gap-3">
                <input
                  type="number"
                  value={pendingNewObjective.effort}
                  onChange={(e) => handleNewObjectiveChange("effort", e.target.value)}
                  onFocus={() => {
                    if (pendingNewObjective.effort === 0) {
                      handleNewObjectiveChange("effort", "");
                    }
                  }}
                  onBlur={() => {
                    if (pendingNewObjective.effort === "") {
                      handleNewObjectiveChange("effort", "0");
                    }
                  }}
                  min="0"
                  className="h-11 rounded-xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-800 outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-200"
                />
                {predictedTokens != null && (
                  <button
                    type="button"
                    className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-100 px-3 py-2 text-sm font-medium text-blue-700 shadow-sm transition hover:border-blue-300 hover:bg-blue-200"
                    onClick={() => handleNewObjectiveChange("effort", predictedTokens)}
                  >
                    <Sparkles className="h-4 w-4" />
                    Utiliser la suggestion
                  </button>
                )}
              </div>
              <p className="text-xs text-slate-400">
                Ajustez la charge estimée — vous pourrez toujours la modifier plus tard.
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-600">Projet</label>
              <div className="relative">
                <input
                  type="text"
                  value={projectSearch}
                  onChange={handleProjectInputChange}
                  onFocus={openProjectDropdown}
                  onBlur={() => setTimeout(() => closeProjectDropdown(), 120)}
                  placeholder="Sélectionner ou rechercher un projet…"
                  className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 pl-10 text-sm text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-blue-400 focus:ring-2 focus:ring-blue-200"
                  aria-autocomplete="list"
                  aria-expanded={projectDropdownOpen}
                  aria-controls="project-picker-dropdown"
                />
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                {projectDropdownOpen && (
                  <div
                    id="project-picker-dropdown"
                    className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-lg"
                  >
                    {noProjectMatch ? (
                      <div className="px-4 py-3 text-sm text-slate-500">
                        Aucun projet ne correspond à « {projectSearch || ""} ».
                      </div>
                    ) : (
                      projectList.groups.map(group => (
                        <div key={group.label}>
                          <div className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
                            {group.label}
                          </div>
                          {group.items.map(project => {
                            const isSelected =
                              Number(pendingNewObjective.project_id) === Number(project.id);
                            return (
                              <button
                                key={project.id}
                                type="button"
                                className={`flex w-full items-center justify-between px-4 py-2 text-left text-sm transition ${
                                  isSelected
                                    ? "bg-blue-50 text-blue-700"
                                    : "hover:bg-slate-50 text-slate-700"
                                }`}
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => handleProjectSelect(project)}
                              >
                                <span className="flex flex-col">
                                  <span className="font-medium">
                                    {projectPrefix(group.key && group.key !== "all" ? group.key : project.status)}
                                    {project.name}
                                  </span>
                                  <span className="text-xs text-slate-500">
                                    {project.project_number ? `#${project.project_number}` : "Sans numéro"}
                                    {project.client_name ? ` • ${project.client_name}` : ""}
                                  </span>
                                </span>
                                {isSelected && (
                                  <span className="text-xs font-semibold text-blue-500">
                                    Sélectionné
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
              <div className="mt-1 flex gap-2">
                <button
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={toggleProjectDropdown}
                  className="rounded-full border border-slate-200 px-3 py-1 text-xs font-medium text-slate-600 transition hover:bg-slate-100"
                >
                  {projectDropdownOpen ? "Masquer les projets" : "Voir les projets"}
                </button>
                <button
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={closeProjectDropdown}
                  className="rounded-full border border-slate-200 px-3 py-1 text-xs font-medium text-slate-600 transition hover:bg-slate-100"
                >
                  Fermer
                </button>
              </div>
              {hasProjectSelected ? (
                projectParticipants && projectParticipants.length > 0 ? (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {projectParticipants.map((participant) => (
                      <span
                        key={participant.id}
                        className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700"
                        title="Focus sur ce projet"
                      >
                        <Users className="h-3.5 w-3.5" />
                        {participant.name}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="mt-2 text-xs text-slate-500">
                    Aucun focus n'est encore associé à ce projet.
                  </p>
                )
              ) : (
                <p className="mt-2 text-xs text-slate-500">
                  Sélectionnez un projet pour afficher les focus.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-600">Owner de l'objectif</label>
              <select
                value={pendingNewObjective.owner_id || ""}
                onChange={(e) =>
                  handleNewObjectiveChange("owner_id", e.target.value === "" ? null : Number(e.target.value))
                }
                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-700 outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-200"
              >
                <option value="">Aucun owner pour le moment</option>
                {sortedActiveEmployees.map((employee) => (
                  <option key={`objective-owner-${employee.id}`} value={employee.id}>
                    {renderEmployeeOptionLabel(employee)}
                  </option>
                ))}
              </select>
              {sortedActiveEmployees.length === 0 && (
                <p className="text-xs text-rose-500">
                  Aucun employé actif n'est disponible pour être owner.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-600">Sprint</label>
              <select
                value={pendingNewObjective.sprint_id || ""}
                onChange={(e) =>
                  handleNewObjectiveChange("sprint_id", e.target.value ? Number(e.target.value) : null)
                }
                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-700 outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-200"
              >
                <option value="">Pas de sprint pour le moment</option>
                {sprintOptions.map((sprint) => (
                  <option
                    key={sprint.id}
                    value={sprint.id}
                    title={sprintLabel(sprint)}
                  >
                    {sprintLabel(sprint)}
                    {sprint.isCurrent ? " · En cours" : ""}
                  </option>
                ))}
              </select>
            </div>

            {selectedSprint && (
              <p className="text-xs text-blue-600">
                {selectedSprint.isCurrent ? "Sprint en cours · " : ""}
                {sprintLabel(selectedSprint)}
              </p>
            )}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-5">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <h4 className="text-base font-semibold text-slate-700">Sous-tâches</h4>
              <p className="text-sm text-slate-500">
                Décomposez l'objectif pour accélérer la mise en mouvement de l'équipe.
              </p>
            </div>
            <button
              type="button"
              onClick={handleAddSubTask}
              className="inline-flex items-center gap-2 rounded-full bg-blue-500 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-blue-600"
            >
              <ListPlus className="h-4 w-4" />
              Ajouter une sous-tâche
            </button>
          </div>

          <div className="mt-4 space-y-3">
            {subTasks.length === 0 && (
              <div className="rounded-xl border border-dashed border-slate-300 bg-white/60 px-4 py-3 text-sm text-slate-500">
                Aucune sous-tâche pour l'instant. Ajoutez-en pour guider l'équipe étape par étape.
              </div>
            )}

            {subTasks.map((task, index) => (
              <div
                key={`subtask-${index}`}
                className="flex flex-col gap-3 rounded-xl bg-white p-4 shadow-sm md:flex-row md:items-center"
              >
                <div className="flex-1">
                  <label className="sr-only">Titre de la sous-tâche</label>
                  <input
                    type="text"
                    value={task?.name || ""}
                    onChange={(e) => handleSubTaskChange(index, "name", e.target.value)}
                    placeholder={`Sous-tâche ${index + 1}`}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-200"
                  />
                </div>
                <div className="flex flex-col gap-2 md:w-56">
                  <label className="text-xs font-medium text-slate-500">Owner</label>
                  <select
                    value={task?.owner_id ?? ""}
                    onChange={(e) => handleSubTaskChange(index, "owner_id", e.target.value)}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-200"
                  >
                    <option value="">Aucun</option>
                    {sortedActiveEmployees.map((employee) => (
                      <option key={`subtask-owner-${employee.id}`} value={employee.id}>
                        {renderEmployeeOptionLabel(employee)}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveSubTask(index)}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-rose-200 text-rose-500 transition hover:bg-rose-50 hover:text-rose-600"
                  title="Supprimer la sous-tâche"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-col justify-end gap-3 border-t border-slate-200 pt-4 sm:flex-row">
          <button
            onClick={handleCancelNewObjective}
            data-ignore="true"
            className="inline-flex items-center justify-center rounded-full border border-slate-200 bg-white px-5 py-2.5 text-sm font-medium text-slate-600 transition hover:border-slate-300 hover:bg-slate-100"
          >
            Annuler
          </button>
          <button
            onClick={handleSaveNewObjective}
            data-ignore="true"
            className="inline-flex items-center justify-center rounded-full bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-300"
          >
            Ajouter l'objectif
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddObjectiveForm;
